import {
  BaseLayoutSchema,
  BooleanConditionSchema,
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
  IWidgetComponentProps,
  RichTextAdvancedInput,
  RichTextAdvancedSchema,
  StyledDrawerCollapse,
} from '@framework/engine';
import {
  CheckboxInput,
  FormItem,
  MenuPresets,
  SelectInput,
  TextInput,
} from '@framework/form';
import { VerticalSpace } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Collapse } from 'antd';
import { useWatch } from 'react-hook-form';
import { AiFileVerifyConfigSection } from '../../../ai/AiFileVerifyConfigSection';
import {
  IUploadWidget,
  UploadButtonsLayout,
  UploadWidgetKind,
  WidgetUploadType,
} from './IUploadWidget';

const { Panel } = Collapse;

export const uploadInputConfigSchema: yup.ObjectSchema<IUploadWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetUploadType).required(),
    kind: yup
      .string()
      .oneOfEnum(UploadWidgetKind)
      .default(UploadWidgetKind.Button)
      .required()
      .label('Tipo'),
    title: RichTextAdvancedSchema.label('Titolo').optional(),
    publicUploadTitle: RichTextAdvancedSchema
      // Rimuovo il campo se l'omissis è disabilitato
      .when(
        ['directives.omissis.enabled'],
        ([omissisEnabled]: boolean[], schema) => {
          if (omissisEnabled) return schema;
          return schema.strip();
        },
      )
      .default([])
      .optional()
      .label('Titolo documento pubblico'),
    publicHelpText: RichTextAdvancedSchema
      // Rimuovo il campo se l'omissis è disabilitato
      .when(
        ['directives.omissis.enabled'],
        ([omissisEnabled]: boolean[], schema) => {
          if (omissisEnabled) return schema;
          return schema.strip();
        },
      )
      .optional()
      .default([])
      .label('Testo di aiuto documento pubblico'),
    publicUploadLabel: RichTextAdvancedSchema
      // Rimuovo il campo se l'omissis è disabilitato
      .when(
        ['directives.omissis.enabled'],
        ([omissisEnabled]: boolean[], schema) => {
          if (omissisEnabled) return schema;
          return schema.strip();
        },
      )
      .optional()
      .default([])
      .label('Etichetta documento pubblico'),
    layout: BaseLayoutSchema.shape({
      hideLabel: yup.boolean().required().default(false),
      buttonLayout: yup
        .string()
        .oneOfEnum(UploadButtonsLayout)
        .default(UploadButtonsLayout.Vertical)
        .optional()
        .label('Posizionamento pulsanti'),
    })
      .required()
      .noUnknown(),
    isImage: yup
      .boolean()
      .default(false)
      .notRequired()
      .label('Gestisci come immagine'),
    // Regole di visibilità
    visible: BooleanConditionSchema,
    enabled: BooleanConditionSchema,
    aiCheckDocumentType: yup.boolean().default(false).notRequired(),
    aiAllowedDocumentTypes: yup
      .array()
      .of(yup.string().required())
      .notRequired(),
    // Nome del file e descrizione
    internalFileName: yup.string().notRequired().label('Sovrascrivi Nome file'),
    internalDescription: yup
      .string()
      .notRequired()
      .label('Descrizione del file'),
  })
    .required()
    .noUnknown();

const TFormItem = FormItem as typeof FormItem<IUploadWidget>;

export interface IUploadWidgetConfigComponentProps
  extends IWidgetComponentProps<IUploadWidget> {}

export function UploadWidgetConfigComponent(
  props: IUploadWidgetConfigComponentProps,
) {
  const sensitiveDataUpload = useWatch({
    name: 'directives.omissis.enabled',
    defaultValue: false,
  });

  return (
    <StyledDrawerCollapse
      defaultActiveKey={['general', 'filename', 'sensitive-data', 'options']}
      ghost
    >
      <Panel header="Impostazioni Generali" key="general">
        <TFormItem name="kind" label="Tipo">
          <SelectInput
            name="kind"
            defaultValue={UploadWidgetKind.Button}
            options={[
              {
                label: 'Pulsante di Upload',
                value: UploadWidgetKind.Button,
              },
              // {
              //   label: 'Area di Upload con Drag & Drop',
              //   value: UploadWidgetKind.DragArea,
              // },
            ]}
          />
        </TFormItem>

        <TFormItem name="title" label="Titolo">
          <RichTextAdvancedInput
            name="title"
            rows={2}
            menus={MenuPresets.Minimal}
          />
        </TFormItem>

        {/* Configurazioni comuni */}
        <CommonConfigComponent
          {...props}
          hideConfigPlaceholder
          hideConfigDefaultValue
        />

        <TFormItem name="layout.hideLabel" layout={{ removeLabelSpace: true }}>
          <CheckboxInput name="layout.hideLabel" defaultChecked={false}>
            Nascondi etichetta
          </CheckboxInput>
        </TFormItem>
        <TFormItem
          name="isImage"
          layout={{ removeLabelSpace: true }}
          help={
            <span>
              Se abilitato, sia in fase di upload che di visualizzazione il file
              verrà visualizzato come un'anteprima grafica. Assicurarsi di
              configurare i <b>Formati file ammessi</b> per permettere solo file
              di tipo <b>immagine</b> (es. jpeg, png, gif)
            </span>
          }
        >
          <CheckboxInput name="isImage" defaultChecked={false}>
            Gestisci come immagine
          </CheckboxInput>
        </TFormItem>
      </Panel>

      <Panel header="Nome file e descrizione" key="filename">
        <TFormItem
          name="internalFileName"
          label="Sovrascrivi Nome file"
          help="Sovrascrive il nome del file caricato dall'utente con il nome indicato. Indicare il nome senza l'estensione: verrà calcolata automaticamente in base al tipo di file caricato dall'utente."
        >
          <TextInput name="internalFileName" />
        </TFormItem>
        <TFormItem
          name="internalDescription"
          label="Descrizione del file"
          help="Descrizione del file da inviare al protocollo"
        >
          <TextInput name="internalDescription" />
        </TFormItem>
      </Panel>

      {sensitiveDataUpload && (
        <Panel
          header="Impostazioni pulsante upload per Dati Sensibili"
          key="sensitive-data"
        >
          <Card size="small">
            <TFormItem
              name="publicUploadTitle"
              label="Titolo documento pubblico"
            >
              <RichTextAdvancedInput
                name="publicUploadTitle"
                rows={2}
                menus={MenuPresets.Minimal}
              />
            </TFormItem>
            <TFormItem
              name="publicUploadLabel"
              label="Etichetta documento pubblico"
            >
              <RichTextAdvancedInput
                name="publicUploadLabel"
                rows={2}
                menus={MenuPresets.Minimal}
              />
            </TFormItem>
            <TFormItem
              name="publicHelpText"
              label="Testo di aiuto documento pubblico"
            >
              <RichTextAdvancedInput
                name="publicHelpText"
                rows={2}
                menus={MenuPresets.Minimal}
              />
            </TFormItem>

            <TFormItem
              name="layout.buttonLayout"
              label="Posizionamento pulsante"
            >
              <SelectInput
                name="layout.buttonLayout"
                defaultValue={UploadButtonsLayout.Vertical}
                options={[
                  {
                    label: 'In Verticale | Pulsanti sovrapposti',
                    value: UploadButtonsLayout.Vertical,
                  },
                  {
                    label: 'In Orizzontale | Pulsanti affiancati',
                    value: UploadButtonsLayout.Horizontal,
                  },
                ]}
              />
            </TFormItem>
          </Card>
        </Panel>
      )}

      {sensitiveDataUpload && <VerticalSpace />}
      {process.env.AI_VERIFY_DOCUMENTS && <AiFileVerifyConfigSection />}
    </StyledDrawerCollapse>
  );
}
